import React, { useCallback } from 'react';
import * as localization from '@happylife-a/localization';
import appBranding from '@happylife-a/branding';
import firebase from '@happylife-a/firebase';

import webApp from '@happylife-a/web-app';
import webCore from '@happylife-a/web-core';
import { Box, Flex, Checkbox, Icon } from '@chakra-ui/react';

function ProductImageAndBadges({
  selectable,
  product,
  productDetail,
  isSelected,
  onSelect,
  displayProductInfo,
  openQuickViewModal,
}) {
  const { locale } = webCore.contexts.useTranslation();
  const { isUnknownUser, isGuestUser } = webCore.contexts.useUser();
  const { dispatch } = webApp.contexts.useModal();
  const { toggleFavorite } = webCore.contexts.useFavorites();

  const onFavoriteToggle = useCallback(() => {
    toggleFavorite(productDetail.id, {
      onCreate: ({ id: favoriteId }) => {
        if (!isGuestUser && appBranding.tools.features.isEnabled('Collection')) {
          dispatch({
            type: 'open',
            modal: webApp.constants.ENUM_MODAL_PRODUCT_ADD_COLLECTION,
            props: {
              favoriteId: favoriteId,
              productDetailId: productDetail.id,
            },
          });
        }
        firebase.libraries.analytics.event.log(webCore.analytics.ANALYTICS_FAVORITE_ADD, {
          productId: productDetail.id,
        });
      },
      onDelete: () => {
        firebase.libraries.analytics.event.log(webCore.analytics.ANALYTICS_FAVORITE_REMOVE, {
          productId: productDetail.id,
        });
      },
    });
  }, [productDetail.id, isGuestUser]);

  const onSignInClick = useCallback(() => {
    const onCloseModal = () => dispatch({ type: 'close' });

    dispatch({
      type: 'open',
      modal: webApp.constants.ENUM_MODAL_SIGN_IN,
      props: { onClose: onCloseModal },
    });
  }, []);

  return (
    <Box w="100%" position="relative" aspectRatio={1.125}>
      <Flex
        position="absolute"
        top={0}
        left={0}
        zIndex={1}
        alignItems="center"
        justifyContent="space-between"
      >
        {Boolean(productDetail.discountPercentage) && (
          <webApp.components.DiscountBadge
            variant="variant1"
            text={`-${productDetail.discountPercentage}%`}
          />
        )}
      </Flex>
      {selectable && (
        <Box position="absolute" right={1} top={2}>
          <Checkbox
            size="sm"
            colorScheme="black.50"
            borderColor="black.50"
            variant="circular"
            onChange={() => onSelect(productDetail)}
            isChecked={isSelected}
          />
        </Box>
      )}
      <Box
        cursor="pointer"
        onClick={!selectable ? displayProductInfo : undefined}
        height="100%"
        overflow="hidden"
        bg="grey.50"
      >
        <Box
          h="100%"
          transition="transform 0.3s ease-in-out"
          _hover={{
            transform: 'scale(1.05)',
          }}
        >
          <webApp.components.S3Image
            folder={webApp.components.S3Image.folders.FOLDER_PRODUCTS}
            imageKey={productDetail?.images?.[0]?.imageKey || product?.images?.[0]?.imageKey || ''}
            size="@0.25"
            alt={
              localization.localize(productDetail?.title, locale) ||
              localization.localize(product?.title, locale)
            }
            borderRadius={{ base: 0, md: '10px 10px 0 0' }}
            objectFit={'contain'}
            w="100%"
            h="100%"
            transition="transform 0.3s ease-in-out"
          />
        </Box>
      </Box>
      <Flex pos="absolute" top={2} right={2} gap={1}>
        <Flex
          display={{ base: 'none', md: 'flex' }}
          w={7}
          h={7}
          borderRadius="full"
          alignItems="center"
          justifyContent="center"
          bgColor="white"
          boxShadow="0px 4px 6px rgba(0, 0, 0, 0.1)"
          onClick={openQuickViewModal}
          cursor="pointer"
        >
          <Icon as={webApp.theme.icons.EyeIconV2} stroke="gray.400" />
        </Flex>
        <Flex
          w={7}
          h={7}
          borderRadius="full"
          alignItems="center"
          justifyContent="center"
          bgColor="white"
          boxShadow="0px 4px 6px rgba(0, 0, 0, 0.1)"
        >
          <webApp.components.FavoriteButton
            variant="variant1"
            productDetailId={productDetail?.id}
            onToggle={isUnknownUser ? onSignInClick : onFavoriteToggle}
          />
        </Flex>
      </Flex>
    </Box>
  );
}

export default ProductImageAndBadges;
